import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  helpArticleBySlug,
  sessionCanOpenHelpHref,
  sessionCanReadHelp,
  type SessionCode,
  type SessionUser,
} from '@inventohub/shared';
import { HELP_DOCUMENTS } from './load';

type Block =
  { type: 'p'; text: string } | { type: 'ol'; items: string[] } | { type: 'ul'; items: string[] };

export function HelpMarkdown({
  source,
  user,
  code,
}: {
  source: string;
  user: SessionUser | null;
  code: SessionCode | null;
}): JSX.Element | null {
  if (!source.trim()) return null;
  const blocks = parseBlocks(source);
  if (blocks.length === 0) return null;
  return (
    <div className="help-prose">
      {blocks.map((block, index) => renderBlock(block, index, user, code))}
    </div>
  );
}

function parseBlocks(source: string): Block[] {
  const lines = source.split(/\r?\n/);
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      i += 1;
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\d+\.\s/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^[-*]\s/, ''));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !/^\d+\.\s/.test(lines[i] ?? '') &&
      !/^[-*]\s/.test(lines[i] ?? '')
    ) {
      para.push(lines[i] ?? '');
      i += 1;
    }
    blocks.push({ type: 'p', text: para.join(' ') });
  }
  return blocks;
}

function renderBlock(
  block: Block,
  index: number,
  user: SessionUser | null,
  code: SessionCode | null,
): JSX.Element | null {
  if (block.type === 'p') {
    return (
      <p key={index}>
        <Inline text={block.text} user={user} code={code} />
      </p>
    );
  }
  const Tag = block.type === 'ol' ? 'ol' : 'ul';
  const items = block.items
    .map((item, itemIndex) => {
      if (isHiddenRelatedItem(item, user, code)) return null;
      if (isHiddenOpenOnlyItem(item, user, code)) return null;
      return (
        <li key={itemIndex}>
          <Inline text={item} user={user} code={code} />
        </li>
      );
    })
    .filter(Boolean);
  if (items.length === 0) return null;
  return <Tag key={index}>{items}</Tag>;
}

function isHiddenRelatedItem(
  item: string,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  const onlyLink = item.trim().match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (!onlyLink) return false;
  const href = onlyLink[2] ?? '';
  if (!href.startsWith('/help/')) return false;
  const slug = href.slice('/help/'.length);
  const target = helpArticleBySlug(HELP_DOCUMENTS, slug);
  if (!target) return true;
  return !sessionCanReadHelp(target, user, code);
}

function isHiddenOpenOnlyItem(
  item: string,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  const onlyLink = item.trim().match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (!onlyLink) return false;
  const label = onlyLink[1] ?? '';
  const href = onlyLink[2] ?? '';
  if (!label.startsWith('Open ')) return false;
  return !sessionCanOpenHelpHref(href, user, code);
}

function Inline({
  text,
  user,
  code,
}: {
  text: string;
  user: SessionUser | null;
  code: SessionCode | null;
}): JSX.Element {
  return <>{renderInline(text, user, code)}</>;
}

function renderInline(
  text: string,
  user: SessionUser | null,
  code: SessionCode | null,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      const label = match[2] ?? '';
      const href = match[3] ?? '';
      const link = renderLink(label, href, user, code, key);
      if (link) nodes.push(link);
    }
    key += 1;
    last = match.index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderLink(
  label: string,
  href: string,
  user: SessionUser | null,
  code: SessionCode | null,
  key: number,
): ReactNode {
  if (href.startsWith('/help/')) {
    const slug = href.slice('/help/'.length);
    const target = helpArticleBySlug(HELP_DOCUMENTS, slug);
    if (!target || !sessionCanReadHelp(target, user, code)) return null;
    return (
      <Link key={key} to={href} data-testid={`help-related-${slug}`}>
        {label}
      </Link>
    );
  }
  if (label.startsWith('Open ')) {
    if (!sessionCanOpenHelpHref(href, user, code)) return null;
    const testId = `help-open-${href.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '')}`;
    return (
      <Link key={key} to={href} className="button button--compact help-open" data-testid={testId}>
        {label}
      </Link>
    );
  }
  if (href.startsWith('/')) {
    return (
      <Link key={key} to={href}>
        {label}
      </Link>
    );
  }
  return (
    <a key={key} href={href} rel="noreferrer">
      {label}
    </a>
  );
}
